import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Check, Minus, Sparkles, Store } from 'lucide-react';
import { Reveal } from '../Reveal';
import { VexuMark } from '../Brand';
import { TalabChapterFrame } from './TalabChapterFrame';
import { openExternal, TALAB_URL } from '../../../data/presentationLinks';
import { MARKETPLACE_PRODUCTS, NOVA_PRODUCTS } from '../../../data/talabProducts';
const EASE = [0.23, 1, 0.32, 1] as const;
const CATALOGUE = NOVA_PRODUCTS.slice(0, 4);
const MARKETPLACE_GRID = [{
  product: NOVA_PRODUCTS[0],
  mine: true
}, {
  product: MARKETPLACE_PRODUCTS[0],
  mine: false
}, {
  product: NOVA_PRODUCTS[2],
  mine: true
}, {
  product: MARKETPLACE_PRODUCTS[1],
  mine: false
}, {
  product: MARKETPLACE_PRODUCTS[2],
  mine: false
}, {
  product: NOVA_PRODUCTS[3],
  mine: true
}, {
  product: MARKETPLACE_PRODUCTS[3],
  mine: false
}, {
  product: MARKETPLACE_PRODUCTS[4],
  mine: false
}];
function DestinationBadge({
  label,
  on
}: {
  label: string;
  on: boolean;
}) {
  return <span className={`flex items-center gap-[4px] rounded-full px-[7px] py-[3px] text-[8.5px] font-semibold tracking-[0.02em] ${on ? 'bg-vexu/10 text-vexu-700' : 'bg-slate-100 text-slate-400'}`} data-id="element-781" data-mp="1w6q3lxu1rh66">
      {on ? <Check className="h-[8px] w-[8px]" data-id="element-782" data-mp="1r8obknen87ik" /> : <Minus className="h-[8px] w-[8px]" data-id="element-783" data-mp="1r97sc0sk4pv0" />}
      {label}
    </span>;
}
interface TalabMarketplaceSlideProps {
  onExit: () => void;
}
export function TalabMarketplaceSlide({
  onExit
}: TalabMarketplaceSlideProps) {
  return <TalabChapterFrame onExit={onExit} data-id="element-784" data-mp="25xksl9yoguvb">
      <Reveal delay={0} data-id="element-785" data-mp="25wj5260h6fwp">
        <h2 className="text-[36px] font-semibold leading-[1.08] tracking-[-0.03em] text-slate-900" data-id="element-786" data-mp="25xb0ngu2odmz">
          Your Storefront. And a Marketplace.
        </h2>
        <p className="mt-[8px] text-[15px] font-light text-slate-500" data-id="element-787" data-mp="ui5we8eam719">
          One catalogue. Two powerful ways to reach customers.
        </p>
      </Reveal>

      <div className="mt-[24px] flex gap-[20px]" data-id="element-788" data-mp="zf5r5n5oznd2">
        {/* Catalogue */}
        <Reveal delay={0.08} className="w-[382px]" data-id="element-789" data-mp="315wgw3afo8b6">
          <div className="rounded-2xl border border-slate-900/[0.08] bg-white p-[16px] shadow-[0_20px_46px_-26px_rgba(15,23,42,0.3)]" data-id="element-790" data-mp="2w733eav5chb0">
            <div className="flex items-baseline justify-between" data-id="element-791" data-mp="2rfbl0nm59hvy">
              <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400" data-id="element-792" data-mp="2nfsy5mariwqh">
                Your Catalogue
              </span>
              <span className="text-[10.5px] font-light text-slate-400" data-id="element-793" data-mp="2ie3p2c73ot51">
                Nova Sportswear
              </span>
            </div>

            <div className="mt-[13px] grid grid-cols-2 gap-[11px]" data-id="element-794" data-mp="28hh6ju28ye66">
              {CATALOGUE.map((product, index) => <motion.div key={product.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              ease: EASE,
              delay: 0.14 + index * 0.07
            }} className="overflow-hidden rounded-xl border border-slate-900/[0.07] bg-white" data-id="element-795" data-mp="24hqhbusdc63r" data-mpi={index}>
                  <img src={product.image} alt={product.name} className="h-[78px] w-full object-cover" data-id="element-796" data-mp="1pjfdvmw5xpvl" />
                  <div className="px-[10px] pb-[10px] pt-[8px]" data-id="element-797" data-mp="1yg6duadu5ovg">
                    <div className="text-[11.5px] font-semibold tracking-[-0.01em] text-slate-900" data-id="element-798" data-mp="11p4hwhzkn61p">
                      {product.name}
                    </div>
                    <div className="mt-[2px] text-[10.5px] text-slate-500" data-id="element-799" data-mp="1aor6u4b46jdv">
                      {product.price}
                    </div>
                    <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 0.28,
                  ease: EASE,
                  delay: 0.82
                }} className="mt-[8px] flex flex-wrap gap-[5px]" data-id="element-800" data-mp="1fodkgeuuu1n7">
                      <DestinationBadge label="My Store" on data-id="element-801" data-mp="2pmdbmdufwlsw" />
                      <DestinationBadge label="Marketplace" on={Boolean(product.marketplace)} data-id="element-802" data-mp="2kmr0c9y69fmq" />
                    </motion.div>
                  </div>
                </motion.div>)}
            </div>

            <p className="mt-[13px] text-[11px] font-light leading-[1.5] text-slate-500" data-id="element-803" data-mp="2un1aflowu6xo">
              Publish selected products — or your entire catalogue.
            </p>
          </div>
        </Reveal>

        {/* Flow */}
        <div className="relative w-[46px]" data-id="element-804" data-mp="3iaraevtcrm5i">
          <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          ease: EASE,
          delay: 0.3
        }} className="absolute left-0 top-[86px] h-px w-full bg-gradient-to-r from-slate-200 to-vexu/60" data-id="element-805" data-mp="3i9g7szbn8kfz" />
          <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3,
          ease: EASE,
          delay: 0.56
        }} className="absolute left-0 top-[300px] h-px w-full bg-gradient-to-r from-slate-200 to-vexu/60" data-id="element-806" data-mp="3md5s3clacfv7" />
        </div>

        {/* Destinations */}
        <div className="flex flex-1 flex-col gap-[16px]" data-id="element-807" data-mp="tn1hfk86reo">
          {/* Storefront */}
          <Reveal delay={0.34} data-id="element-808" data-mp="4rcg4x5cika1">
            <div className="rounded-2xl border border-slate-900/[0.08] bg-white p-[16px] shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-id="element-809" data-mp="3n756dc7u1s5j">
              <div className="flex items-baseline justify-between" data-id="element-810" data-mp="3ry54ulwloia5">
                <h3 className="text-[17px] font-semibold tracking-[-0.02em] text-slate-900" data-id="element-811" data-mp="3d8z1qmun67wz">
                  Your Conversational Storefront
                </h3>
                <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400" data-id="element-812" data-mp="3i8brbfcwdt39">
                  Private Link • Website • Widget
                </span>
              </div>

              <div className="mt-[12px] flex items-center gap-[12px]" data-id="element-813" data-mp="396u9nsjudjcg">
                <span className="flex items-center gap-[8px] rounded-full border border-vexu/25 bg-vexu/[0.06] px-[13px] py-[8px] text-[11.5px] text-slate-700" data-id="element-814" data-mp="1es71vghns5mv">
                  <Sparkles className="h-[12px] w-[12px] text-vexu-700" data-id="element-815" data-mp="151c4bfyypjm4" />
                  “Something cushioned for daily runs?”
                </span>
                <div className="flex gap-[8px]" data-id="element-816" data-mp="11121gc6gv9bh">
                  {CATALOGUE.slice(0, 3).map(product => <img key={product.id} src={product.image} alt={product.name} className="h-[54px] w-[62px] rounded-lg object-cover ring-1 ring-slate-900/[0.06]" data-id="element-817" data-mp="1xp89za6ielv1" />)}
                </div>
                <span className="ml-auto text-right text-[11px] font-light leading-[1.5] text-slate-500" data-id="element-818" data-mp="1jscm4bllmpux">
                  Your own branded
                  <br data-id="element-819" data-mp="8atp50ym3j4t" />
                  customer experience
                </span>
              </div>
            </div>
          </Reveal>

          {/* Marketplace */}
          <Reveal delay={0.58} data-id="element-820" data-mp="2oxj8jmppu1oj">
            <div className="rounded-2xl border border-vexu/35 bg-white p-[16px] shadow-[0_22px_54px_-26px_rgba(0,200,83,0.45)]" data-id="element-821" data-mp="2owqbxekh0sk4">
              <div className="flex items-baseline justify-between" data-id="element-822" data-mp="2sxk0891ts9dj">
                <h3 className="flex items-center gap-[9px] text-[17px] font-semibold tracking-[-0.02em] text-slate-900" data-id="element-823" data-mp="2xq3aaw1b0mdl">
                  <Store className="h-[15px] w-[15px] text-vexu-600" data-id="element-824" data-mp="32o4wvqxxv3tt" />
                  Talab Marketplace
                </h3>
                <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400" data-id="element-825" data-mp="37q3xwu67honl">
                  Discoverable across Talab · 1,200+ merchants
                </span>
              </div>

              <div className="mt-[12px] flex items-center gap-[10px] rounded-full border border-slate-900/[0.07] bg-slate-50 px-[14px] py-[8px]" data-id="element-826" data-mp="3gmcwy7q9ul97">
                <span className="h-[5px] w-[5px] rounded-full bg-vexu" data-id="element-827" data-mp="1mbprw6pbkz4y" />
                <span className="text-[11px] text-slate-500" data-id="element-828" data-mp="13e64flnvlzef">
                  Best marathon shoes under AED 600 — anywhere on Talab
                </span>
              </div>

              <div className="mt-[12px] grid grid-cols-8 gap-[9px]" data-id="element-829" data-mp="ydic8wnwvcim">
                {MARKETPLACE_GRID.map((entry, index) => <motion.div key={`${entry.product.id}-${index}`} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.28,
                ease: EASE,
                delay: 0.68 + index * 0.05
              }} className={`overflow-hidden rounded-lg bg-white ${entry.mine ? 'ring-2 ring-vexu/60' : 'ring-1 ring-slate-900/[0.07]'}`} data-id="element-830" data-mp="184min7zitp1u" data-mpi={index}>
                    <img src={entry.product.image} alt={entry.product.name} className="h-[62px] w-full object-cover" data-id="element-831" data-mp="1zvitk320tjmh" />
                    <div className="px-[6px] pb-[7px] pt-[5px]" data-id="element-832" data-mp="2cfpj63bz82ae">
                      <div className="truncate text-[8px] font-medium text-slate-600" data-id="element-833" data-mp="1yfoj2q3jcyvz">
                        {entry.product.name}
                      </div>
                      <div className="mt-[1px] text-[8px] font-semibold text-slate-900" data-id="element-834" data-mp="23gca3brrlfq7">
                        {entry.product.price}
                      </div>
                    </div>
                  </motion.div>)}
              </div>

              <div className="mt-[11px] flex items-center gap-[9px]" data-id="element-835" data-mp="2hczicap63sbf">
                <span className="h-[9px] w-[9px] rounded-[3px] ring-2 ring-vexu/60" data-id="element-836" data-mp="2mcu8gc14cdvf" />
                <span className="text-[10.5px] font-light text-slate-500" data-id="element-837" data-mp="3a2okirdm5yem">
                  Your published products, discovered alongside the wider Talab
                  marketplace.
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="flex-1" data-id="element-838" data-mp="2tnlsh4f7zir1" />

      <Reveal delay={1} data-id="element-839" data-mp="2ypb2qhumbse2">
        <div className="h-px w-full bg-slate-900/[0.10]" data-id="element-840" data-mp="2ynhtqvwj8w2w" />
        <div className="flex items-end justify-between pt-[20px]" data-id="element-841" data-mp="2kpa1v2ecskyl">
          <div data-id="element-842" data-mp="2koqf98drd8e8">
            <p className="max-w-[760px] text-[20px] font-light leading-[1.4] tracking-[-0.015em] text-slate-500" data-id="element-843" data-mp="2krcmt80nfnwg">
              One catalogue. Your own conversational storefront.{' '}
              <span className="font-medium text-slate-900" data-id="element-844" data-mp="2ax3ykwinih43">
                Plus access to the Talab marketplace.
              </span>
            </p>
            <div className="mt-[14px] flex items-center gap-[13px]" data-id="element-845" data-mp="21zbk1ukr7dhr">
              <span className="text-[13px] font-semibold tracking-[0.26em] text-slate-900" data-id="element-846" data-mp="2603tooxjfkek">
                TALAB
              </span>
              <span className="h-[12px] w-px bg-slate-300" data-id="element-847" data-mp="1s1dvt72w9gp5" />
              <span className="flex items-center gap-[7px]" data-id="element-848" data-mp="1wzfp0ku1awec">
                <VexuMark size={15} data-id="element-849" data-mp="1wyw9faruwk3h" />
                <span className="text-[10.5px] font-medium tracking-[0.14em] text-slate-500" data-id="element-850" data-mp="1ddnn76b6sr3o">
                  by Vexu AI
                </span>
              </span>
            </div>
          </div>

          <div className="text-right" data-id="element-851" data-mp="1nbtnxkl9br6y">
            <p className="mb-[11px] text-[12px] font-light text-slate-500" data-id="element-852" data-mp="1ncuw9haw1zkz">
              See conversational commerce in action.
            </p>
            <button type="button" onClick={() => openExternal(TALAB_URL)} className="group inline-flex items-center gap-[11px] rounded-xl bg-vexu-600 px-[26px] py-[15px] text-[15px] font-semibold text-white shadow-[0_16px_36px_-16px_rgba(0,153,64,0.75)] transition-[background-color,transform] duration-200 ease-out hover:bg-vexu-700 active:scale-[0.99]" data-id="element-853" data-mp="1if38mzha0zml">
              Experience Talab Live
              <ArrowUpRight className="h-[17px] w-[17px] transition-transform duration-200 ease-out group-hover:translate-x-[3px] group-hover:-translate-y-[2px]" data-id="element-854" data-mp="2f4130gl46vj8" />
            </button>
          </div>
        </div>
      </Reveal>
    </TalabChapterFrame>;
}